'use client';

import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import type { ColumnDef, ColumnFiltersState, SortingState, VisibilityState, Table as TanStackTable } from '@tanstack/react-table';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataTablePagination } from './pagination';
import { cn } from '@/lib/utils';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  // Configuration options
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enablePagination?: boolean;
  enableColumnVisibility?: boolean;
  enableRowSelection?: boolean;
  enableTableHeader?: boolean;
  // Pagination options
  pageSize?: number;
  pageSizeOptions?: number[];
  // Filter options
  filterColumn?: string;
  filterPlaceholder?: string;
  // Callbacks
  onRowClick?: (row: TData) => void;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  getRowClassName?: (row: TData) => string;
  // Custom toolbar
  toolbar?: (table: TanStackTable<TData>) => React.ReactNode;
  // Empty state
  emptyMessage?: string;
  // Loading state
  isLoading?: boolean;
  // Custom className
  className?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  enableSorting = true,
  enableFiltering = false,
  enablePagination = true,
  enableColumnVisibility = false,
  enableRowSelection = false,
  enableTableHeader = true,
  pageSize = 10,
  pageSizeOptions = [10, 20, 30, 40, 50],
  filterColumn,
  filterPlaceholder = 'Filter...',
  onRowClick,
  onRowSelectionChange,
  getRowClassName,
  toolbar,
  emptyMessage = 'No results.',
  isLoading = false,
  className
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Column sizing
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 30,
      maxSize: 10000
    },
    // Sorting
    ...(enableSorting && {
      onSortingChange: setSorting,
      getSortedRowModel: getSortedRowModel()
    }),
    // Filtering
    ...(enableFiltering && {
      onColumnFiltersChange: setColumnFilters,
      getFilteredRowModel: getFilteredRowModel()
    }),
    // Pagination
    ...(enablePagination && {
      getPaginationRowModel: getPaginationRowModel(),
      initialState: {
        pagination: {
          pageSize
        }
      }
    }),
    // Column visibility
    ...(enableColumnVisibility && {
      onColumnVisibilityChange: setColumnVisibility
    }),
    // Row selection
    ...(enableRowSelection && {
      onRowSelectionChange: setRowSelection,
      enableRowSelection: true
    }),
    state: {
      ...(enableSorting && { sorting }),
      ...(enableFiltering && { columnFilters }),
      ...(enableColumnVisibility && { columnVisibility }),
      ...(enableRowSelection && { rowSelection })
    }
  });

  // Handle row selection change callback
  React.useEffect(() => {
    if (enableRowSelection && onRowSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
      onRowSelectionChange(selectedRows);
    }
  }, [rowSelection, enableRowSelection, onRowSelectionChange, table]);

  return (
    <div className={className}>
      {/* Toolbar */}
      {toolbar && <div className='mb-4'>{toolbar(table)}</div>}

      {/* Table */}
      <div className='overflow-hidden'>
        <Table style={{ tableLayout: 'fixed', width: '100%' }} className='space-y-1'>
          {enableTableHeader && (
            <TableHeader className='rounded-lg'>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className='rounded-lg'>
                  {headerGroup.headers.map((header, index) => {
                    const isFirst = index === 0;
                    const isLast = index === headerGroup.headers.length - 1;
                    return (
                      <TableHead
                        key={header.id}
                        style={{ width: `${header.getSize()}px` }}
                        className={cn('px-3 h-8', isFirst && 'rounded-l-lg', isLast && 'rounded-r-lg')}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
          )}
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className='h-48 text-center'>
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, rowIndex) => {
                const isLastRow = rowIndex === table.getRowModel().rows.length - 1;
                const isCategory = (row.original as any)?.type === 'category';
                const nextRowIsCategory = !isLastRow && (table.getRowModel().rows[rowIndex + 1].original as any)?.type === 'category';
                
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      data-state={row.getIsSelected() && 'selected'}
                      onClick={() => onRowClick?.(row.original)}
                      className={cn('border-none', onRowClick && 'cursor-pointer', getRowClassName?.(row.original))}
                    >
                      {row.getVisibleCells().map((cell, index) => {
                        const isFirst = index === 0;
                        const isLast = index === row.getVisibleCells().length - 1;
                        return (
                          <TableCell
                            key={cell.id}
                            style={{ width: `${cell.column.getSize()}px`,}}
                            className={cn(
                              'text-black-50 text-xs',
                              isCategory ? 'p-2 h-[30px]' : 'max-h-12 p-3',
                              isFirst && 'rounded-l-lg',
                              isLast && 'rounded-r-lg'
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {!isLastRow && !isCategory && !nextRowIsCategory && (
                      <tr>
                        <td colSpan={columns.length} className='p-0'>
                          <div className='border-b mx-2 my-0.5 border-grey-300' />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className='h-80 text-center text-xs'>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {enablePagination && !isLoading && data.length > 0 && (
        <div className='mt-4'>
          <DataTablePagination table={table} pageSizeOptions={pageSizeOptions} showRowSelection={enableRowSelection} />
        </div>
      )}
    </div>
  );
}
